import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { removeCart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadCurrentAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadCurrentAccount";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCart";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { RemoveCart } from "@insite/client-framework/Store/Data/Carts/Handlers/RemoveCart";
import loadOrderApprovals from "@insite/client-framework/Store/Data/OrderApprovals/Handlers/LoadOrderApprovals";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import TextArea from "@insite/mobius/TextArea";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getOrderApprovalNotes from "@tKgpco/Handlers/OrderApproval/getOrderApprovalNotesHandler";
import updateOrderApprovalStatus from "@tKgpco/Handlers/OrderApproval/updateOrderApprovalStatusHandler";
import { cancelOrderApproval, loadOrderApprovalNotesResponse } from "@tKgpco/Services/OrderApprovalService";
import React, { FunctionComponent, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    cart: getCurrentCartState(state).value,
    isCheckoutReviewPage: getCurrentPage(state)?.type === "CheckoutReviewAndSubmitPage",
    userName: state.context.session.userName,
    getOrderApprovalsParameter: state.pages.orderApprovalList.getOrderApprovalsParameter,
    orderApprovalListPage: getPageLinkByPageType(state, "OrderApprovalListPage"),
    userRoles: state.context.session.userRoles,
    account: getCurrentAccountState(state)?.value,
});

const mapDispatchToProps = {
    getOrderApprovalNotes,
    updateOrderApprovalStatus,
    loadOrderApprovals,
    loadCurrentAccount,
    loadCart,
    loadCurrentCart,
};

export interface CartApproverNotesStyles {
    approvingText?: TypographyPresentationProps;
    approvingWrapper?: InjectableCss;
    container?: InjectableCss;
    ctaContainer?: InjectableCss;
    allNotesContainer?: InjectableCss;
    contentText?: TypographyPresentationProps;
}

export const CartApproverNotesStyles: CartApproverNotesStyles = {
    approvingWrapper: {
        css: css`
            text-align: center;
            padding: 10px;
            background-color: ${getColor("info.main")};
        `,
    },
    approvingText: {
        css: css`
            width: 100%;
        `,
    },
    container: {
        css: css`
            margin-top: 20px;
            margin-bottom: 20px;
            display: flex;
            flex-direction: column;
            gap: 10px;
            align-items: flex-start;
            width: 100%;

            textarea {
                width: 100%;
                min-height: 100px;
                resize: none;
            }
        `,
    },
    ctaContainer: {
        css: css`
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            width: 100%;

            button,
            button span {
                font-weight: bold;
                text-transform: uppercase;
            }
        `,
    },
    allNotesContainer: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    contentText: {
        css: css`
            display: block;
            margin-bottom: 11px;
            span {
                &.title {
                    font-weight: bold;
                }
                &.value {
                    margin-left: 0px;
                    text-transform: capitalize;
                }
                font-size: 14px;
            }
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const styles = CartApproverNotesStyles;
const CartApproverNotes = ({
    cart,
    isCheckoutReviewPage,
    getOrderApprovalNotes,
    userName,
    updateOrderApprovalStatus,
    loadOrderApprovals,
    getOrderApprovalsParameter,
    orderApprovalListPage,
    history,
    userRoles,
    account,
    loadCurrentAccount,
    loadCart,
    loadCurrentCart,
}: Props) => {
    const [approverNotes, setApproverNotes] = React.useState("");
    const [approverNotesError, setApproverNotesError] = React.useState(false);
    const [allNotes, setAllNotes] = React.useState<loadOrderApprovalNotesResponse[]>([]);
    const [isReviewingOrder, setIsReviewingOrder] = React.useState(false);
    const [isCancelingOrder, setIsCancelingOrder] = React.useState(false);
    const [isRequisitioner, setIsRequisitioner] = React.useState(false);

    useEffect(() => {
        if (localStorage.getItem("approverNotes")) {
            setApproverNotes(localStorage.getItem("approverNotes") || "");
        }
        // localStorage.removeItem("orderNotes");
    }, []);

    React.useEffect(() => {
        if (!account) {
            loadCurrentAccount();
        }
    }, [account]);

    useEffect(() => {
        if (userRoles && userRoles.length > 0) {
            if (userRoles.includes("Requisitioner") || userRoles.includes("Buyer1")) {
                setIsRequisitioner(true);
            } else {
                setIsRequisitioner(false);
            }
        }
    }, [userRoles]);

    useEffect(() => {
        if (isCheckoutReviewPage && cart && cart.orderNumber && (account?.canApproveOrders || isRequisitioner)) {
            getOrderApprovalNotes({
                orderNumber: cart.orderNumber,
                onSuccess(result) {
                    setAllNotes(result as unknown as loadOrderApprovalNotesResponse[]);
                },
            });
        }
    }, [isCheckoutReviewPage, cart, account, isRequisitioner]);

    if (
        !cart ||
        cart.cartLines?.length === 0 ||
        !isCheckoutReviewPage ||
        (!cart.isAwaitingApproval && !isRequisitioner)
    ) {
        return null;
    }

    const setApproverNotesHandler = (value: string) => {
        if (value.trim().length > 0) {
            setApproverNotesError(false);
        }
        setApproverNotes(value);
    };

    const reviewHandler = () => {
        if (approverNotes.trim().length === 0) {
            setApproverNotesError(true);
            return;
        }

        setIsReviewingOrder(true);
        updateOrderApprovalStatus({
            orderNumber: cart.orderNumber!,
            fromStatus: cart.status!,
            notes: approverNotes,
            approverUserName: userName!,
            cancelOrder: false,
            onSuccess(result) {
                loadOrderApprovals(getOrderApprovalsParameter);
                loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false, getCartLines: true });
                if (orderApprovalListPage?.url) {
                    history.push(orderApprovalListPage?.url);
                }
                setIsReviewingOrder(false);
            },
            onError(message) {
                setIsReviewingOrder(false);
            },
        });
    };

    const cancelHandler = () => {
        if (approverNotes.trim().length === 0) {
            setApproverNotesError(true);
            return;
        }
        setIsCancelingOrder(true);
        updateOrderApprovalStatus({
            orderNumber: cart.orderNumber!,
            fromStatus: cart.status!,
            notes: approverNotes,
            approverUserName: userName!,
            cancelOrder: true,
            onSuccess(result) {
                loadOrderApprovals(getOrderApprovalsParameter);
                loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false, getCartLines: true });
                if (orderApprovalListPage?.url) {
                    history.push(orderApprovalListPage?.url);
                }
                setIsCancelingOrder(false);
            },
            onError(message) {
                setIsCancelingOrder(false);
            },
        });
    };

    return (
        <>
            {cart.isAwaitingApproval && account?.canApproveOrders && (
                <StyledWrapper {...styles.ctaContainer}>
                    <Button
                        variant="secondary"
                        onClick={cancelHandler}
                        icon={{
                            src: isCancelingOrder ? LoadingSpinner : "",
                            position: "left",
                        }}
                    >
                        {translate("Cancel Order")}
                    </Button>
                    <Button
                        onClick={reviewHandler}
                        icon={{
                            src: isReviewingOrder ? LoadingSpinner : "",
                            position: "left",
                        }}
                    >
                        {" "}
                        {translate("Review Order")}{" "}
                    </Button>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.container}>
                {allNotes.length ? (
                    <StyledWrapper {...styles.allNotesContainer}>
                        <Typography variant="h3">{translate("Previous Notes:")}</Typography>
                        {allNotes.map((note, index: number) => {
                            const [key] = Object.keys(note);
                            const value = note[key];

                            return (
                                <Typography key={value} {...styles.contentText}>
                                    <Typography className="title">{`${index + 1}) ${key}`}</Typography>
                                    {value && <Typography className="value">{`:  ${value}`}</Typography>}
                                </Typography>
                            );
                        })}
                    </StyledWrapper>
                ) : null}
                {account?.canApproveOrders || isRequisitioner ? (
                    <TextArea
                        label={<Typography variant="h3">{translate("Note")}</Typography>}
                        value={approverNotes}
                        onChange={e => {
                            setApproverNotesHandler(e.target.value);
                            localStorage.setItem("approverNotes", e.target.value);
                        }}
                        error={approverNotesError && translate("Please add a note")}
                    />
                ) : null}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CartApproverNotes)),
    definition: {
        displayName: "Cart Approver Notes",
        icon: "cart-circle-check",
        group: "Common",
    },
};

export default widgetModule;
